import { useState, useRef, useEffect } from 'react'
import { useGenerator } from '../../../../hooks/useGenerator'
import { useStreamGenerator } from '../../../../hooks/useStreamGenerator'
import { useSettingsStore } from '../../../../stores/settingsStore'
import { useTitleStore } from '../../../../stores/titleStore'
import { usePlotStore } from '../../../../stores/plotStore'
import { useCustomValuesStore } from '../../../../stores/customValuesStore'
import { useWorldBuilderStore } from '../../../../stores/worldbuilderStore'
import { useCharacterStore } from '../../../../stores/characterStore'
import type { TitleInput, TitleResult, SavedTitle } from '@creanizer/types'
import type { CharacterInput, CharacterResult, SavedCharacter } from '../../../../types/character' // temp fix
import type { SavedWorldBuilding } from '@creanizer/types'
import type { CustomValue } from '@creanizer/utils'
import { 
  Button, Badge, Input, Label, Textarea, Tabs, TabsList, TabsTrigger, TabsContent,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Checkbox,
  Alert, AlertDescription, Progress
} from '@creanizer/ui'
import { Save, Download, Trash2, Copy, Upload, FileText, X, Globe, Users, ChevronDown, ChevronUp, Eye, Search, Folder } from 'lucide-react'
import { ExportImportButtons } from '../../../../components/common'
import { useFolderStore } from '../../../../stores/folderStore'
import { FolderManager, FolderMoveSelect } from './FolderManager'
import { ScrollArea } from '@creanizer/ui'

export function TitleGenerator() {
  const [activeTab, setActiveTab] = useState('generate')
  const [result, setResult] = useState<TitleResult | null>(null)
  const [customStyle, setCustomStyle] = useState('')
  const [selectedPlotId, setSelectedPlotId] = useState<string>('')
  const [selectedWorldId, setSelectedWorldId] = useState<string>('')
  const [selectedCharacterIds, setSelectedCharacterIds] = useState<string[]>([])
  const [uploadedFileName, setUploadedFileName] = useState<string>('')
  const [error, setError] = useState<string | null>(null)
  const [showCharacterSelector, setShowCharacterSelector] = useState(false)
  const [characterSearchQuery, setCharacterSearchQuery] = useState('')
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null)
  const [showFolders, setShowFolders] = useState(true)
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  
  // 統一された入力テキストステート
  const [sourceText, setSourceText] = useState<string>('')
  const [sourceInfo, setSourceInfo] = useState<{
    type: 'manual' | 'plot' | 'file' | 'world' | 'characters'
    name?: string
  }>({ type: 'manual' })
  
  const [input, setInput] = useState<TitleInput>({
    inputType: 'text',
    text: '',
    style: 'impact',
    titleType: 'work',
    creativityLevel: 5,
    outputCount: 10,
    useProMode: false,
    useGemini3FlashMode: false,
  })
  const [useStreaming, setUseStreaming] = useState(true)

  const generator = useGenerator('title')
  const streamGenerator = useStreamGenerator('title')
  const { isApiKeySet } = useSettingsStore()
  const { 
    savedTitles, 
    saveTitle, 
    deleteTitle,
    exportAsText,
    exportAsJSON,
    importFromJSON
  } = useTitleStore()
  const { getItemsInFolder, getItemFolder, moveItemBetweenFolders } = useFolderStore()
  const { savedPlots } = usePlotStore()
  const { savedWorlds } = useWorldBuilderStore()
  const { characters } = useCharacterStore()
  
  // カスタム値管理
  const { 
    customValues,
    getCustomValues,
    addCustomValue,
    useCustomValue,
    deleteCustomValue,
    init: initCustomValues
  } = useCustomValuesStore()

  // タイトルジェネレータ用のカスタムスタイルを取得
  const savedStyles = getCustomValues('style', 'title')

  useEffect(() => {
    initCustomValues()
  }, [])

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    
    const reader = new FileReader()
    reader.onload = (event) => {
      const content = event.target?.result as string
      setSourceText(content)
      setUploadedFileName(file.name)
      setSourceInfo({ type: 'file', name: file.name })
      setInput({ ...input, inputType: 'file', text: content })
    }
    reader.readAsText(file)
  }

  const handlePlotSelect = (plotId: string) => {
    setSelectedPlotId(plotId)
    const plot = savedPlots.find(p => p.id === plotId)
    if (plot) {
      const plotText = `
タイトル: ${plot.title}
ジャンル: ${plot.input.genre || plot.input.genreCustom || ''}
舞台: ${plot.input.stage || plot.input.stageCustom || ''}

あらすじ:
${plot.result.synopsis}

章構成:
${plot.result.structure?.map(ch => `${ch.title}: ${ch.summary}`).join('\n') || ''}
      `.trim()
      setSourceText(plotText)
      setSourceInfo({ type: 'plot', name: plot.title })
      setInput({ ...input, inputType: 'plot', plotId, text: plotText })
    }
  }

  const handleWorldSelect = (worldId: string) => {
    setSelectedWorldId(worldId)
    const world = savedWorlds.find(w => w.id === worldId)
    if (world) {
      const worldText = `【世界観：${world.result.name}】\n${JSON.stringify(world.result)}`
      setSourceText(worldText)
      setSourceInfo({ type: 'world', name: world.result.name })
      setInput({ 
        ...input, 
        inputType: 'world', 
        worldId, 
        worldContext: JSON.stringify(world.result),
        text: worldText 
      })
    }
  }

  const handleCharacterToggle = (characterId: string) => {
    const newSelectedCharacters = selectedCharacterIds.includes(characterId)
      ? selectedCharacterIds.filter(id => id !== characterId)
      : [...selectedCharacterIds, characterId]
    
    setSelectedCharacterIds(newSelectedCharacters)
    updateCharactersText(newSelectedCharacters)
  }

  const updateCharactersText = (characterIds: string[]) => {
    if (characterIds.length === 0) {
      setSourceText('')
      setSourceInfo({ type: 'manual' })
      setInput({ ...input, inputType: 'text', text: '', characterIds: [], charactersContext: undefined })
      return
    }

    const selectedChars = characters.filter(c => characterIds.includes(c.id))
    const charactersText = selectedChars.map(char => {
      return `【キャラクター：${char.result.name}】\n${JSON.stringify(char.result)}`
    }).join('\n\n')
    
    setSourceText(charactersText)
    setSourceInfo({ type: 'characters', name: `${characterIds.length}人のキャラクター` })
    setInput({ 
      ...input, 
      inputType: 'characters',
      characterIds,
      charactersContext: JSON.stringify(selectedChars.map(c => c.result)),
      text: charactersText 
    })
  }

  const handleTextChange = (text: string) => {
    setSourceText(text)
    setSourceInfo({ type: 'manual' })
    setInput({ ...input, inputType: 'text', text })
  }

  const clearSource = () => {
    setSourceText('')
    setSelectedPlotId('')
    setSelectedWorldId('')
    setSelectedCharacterIds([])
    setUploadedFileName('')
    setSourceInfo({ type: 'manual' })
    setInput({ ...input, text: '', worldId: undefined, worldContext: undefined, characterIds: [], charactersContext: undefined })
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleGenerate = async () => {
    setError(null)
    setResult(null) // 結果をクリア
    
    if (!isApiKeySet()) {
      setError('APIキーが設定されていません')
      return
    }

    if (!sourceText.trim()) {
      setError('タイトルを生成するための内容を入力してください')
      return
    }

    const finalInput = { 
      ...input,
      text: sourceText,
      // カスタムスタイルが入力されている場合はそれを使用
      // input.styleが'custom'の場合は必ずcustomStyleを使用
      style: input.style === 'custom' ? (customStyle || 'カスタム') : input.style
    }
    
    if (input.inputType === 'plot' && selectedPlotId) {
      finalInput.plotId = selectedPlotId
    }

    try {
      if (useStreaming) {
        await streamGenerator.generateStream(finalInput as any, {
          onChunk: (chunk) => {
            console.log('Received chunk:', chunk.length)
          },
          onProgress: (bytes) => {
            console.log('Progress:', bytes)
          },
          onComplete: (resultData) => {
            if (resultData !== null && resultData !== undefined) {
              setResult(resultData as TitleResult)
            } else {
              console.error('Incomplete result received:', resultData)
              setError('結果のパースに失敗しました')
            }
          },
          onError: (error) => {
            console.error('Streaming error:', error)
            setError(error.message)
            
            if (error.message.includes('ストリーミング')) {
              setUseStreaming(false)
              generator.mutate(finalInput as any)
            }
          }
        })
      } else {
        const response = await generator.mutateAsync(finalInput as any)
        if (response?.result) {
          setResult(response.result as TitleResult)
        }
      }
    } catch (error: any) {
      console.error('Generation failed:', error)
      
      // エラーメッセージを設定
      if (error.message?.includes('Empty response')) {
        setError('生成に失敗しました。もう一度お試しください。')
      } else if (error.message?.includes('rate limit')) {
        setError('APIの利用制限に達しました。しばらく待ってから再試行してください。')
      } else if (error.message?.includes('parse')) {
        setError('生成結果の処理に失敗しました。もう一度お試しください。')
      } else {
        setError('エラーが発生しました: ' + (error.message || '不明なエラー'))
      }
    }
  }

  const isSaved = (candidate: any) => {
    if (!candidate || !candidate.title) return false
    return savedTitles.some(saved => 
      saved?.title === candidate.title
    )
  }

  const handleSave = async (candidate: any) => {
    const savedTitle: SavedTitle = {
      id: crypto.randomUUID(),
      title: candidate.title,
      input: { ...input, text: sourceText },
      result: {
        candidates: [candidate],
        sourceText: sourceText,
        parameters: result?.parameters || {
          style: input.style,
          titleType: input.titleType,
          creativityLevel: input.creativityLevel,
          outputCount: 1,
        },
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }

    try {
      await saveTitle(savedTitle)
      // 状態を更新するために新しいオブジェクトで再レンダリング
      setResult(result ? {...result} : null)
    } catch (error) {
      alert('保存に失敗しました: ' + error)
    }
  }

  // Export/Import handled by ExportImportButtons component

  const handleAddCustomStyle = async () => {
    if (!customStyle.trim()) return
    
    await addCustomValue('style', customStyle, customStyle, 'title')
    // 保存後もカスタムスタイルは維持する
    alert(`スタイル「${customStyle}」を保存しました`)
  }

  const handleSelectSavedStyle = async (style: CustomValue) => {
    await useCustomValue(style.id)
    // カスタムスタイルを選択した場合は、selectを'custom'にして、実際の値をcustomStyleに設定
    setInput({ ...input, style: 'custom' })
    setCustomStyle(style.value)
  }

  const handleDeleteSavedStyle = async (id: string) => {
    if (confirm('このスタイルを削除しますか？')) {
      await deleteCustomValue(id)
    }
  }

  return (
    <div className="space-y-6">
      <div className="card">
        <h2 className="text-xl font-semibold mb-6">タイトルジェネレータ</h2>
        
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="generate">生成</TabsTrigger>
            <TabsTrigger value="saved">保存済み ({savedTitles.length}/{1000})</TabsTrigger>
          </TabsList>

          <TabsContent value="generate" className="space-y-4 mt-4">
            {/* 入力ソース選択 */}
            <div className="space-y-4">
              <Label>入力ソース</Label>
              
              {/* 現在のソース情報 */}
              {sourceInfo.name && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {sourceInfo.type === 'world' ? (
                      <Globe className="h-4 w-4 text-blue-600" />
                    ) : sourceInfo.type === 'characters' ? (
                      <Users className="h-4 w-4 text-blue-600" />
                    ) : (
                      <FileText className="h-4 w-4 text-blue-600" />
                    )}
                    <span className="text-sm">
                      {sourceInfo.type === 'plot' ? 'プロット: ' : 
                       sourceInfo.type === 'world' ? '世界観: ' :
                       sourceInfo.type === 'characters' ? 'キャラクター: ' :
                       'ファイル: '}
                      <strong>{sourceInfo.name}</strong>
                    </span>
                  </div>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={clearSource}
                  >
                    クリア
                  </Button>
                </div>
              )}
              
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  {/* プロット選択 */}
                  <div>
                    <Select value={selectedPlotId} onValueChange={handlePlotSelect}>
                      <SelectTrigger>
                        <SelectValue placeholder="保存済みプロットから選択" />
                      </SelectTrigger>
                      <SelectContent>
                        {savedPlots.length === 0 ? (
                          <div className="p-2 text-sm text-gray-500">
                            保存済みプロットがありません
                          </div>
                        ) : (
                          savedPlots.map((plot: any) => (
                            <SelectItem key={plot.id} value={plot.id}>
                              {plot.title}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* ファイル選択 */}
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".txt,.md"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      ファイルを選択
                    </Button>
                  </div>
                </div>

                {/* 世界観選択 */}
                {savedWorlds.length > 0 && (
                  <div>
                    <Select value={selectedWorldId} onValueChange={handleWorldSelect}>
                      <SelectTrigger>
                        <SelectValue placeholder="保存済み世界観から選択">
                          {selectedWorldId && (
                            <div className="flex items-center gap-2">
                              <Globe className="h-4 w-4" />
                              {savedWorlds.find(w => w.id === selectedWorldId)?.title}
                            </div>
                          )}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {savedWorlds.map((world) => (
                          <SelectItem key={world.id} value={world.id}>
                            <div className="flex flex-col">
                              <div className="font-medium">{world.title}</div>
                              <div className="text-xs text-muted-foreground">
                                {world.result.summary?.substring(0, 50)}...
                              </div>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* キャラクター選択 */}
                {characters.length > 0 && (
                  <div className="space-y-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full justify-between"
                      onClick={() => setShowCharacterSelector(!showCharacterSelector)}
                    >
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        <span>保存済みキャラクターから選択</span>
                        {selectedCharacterIds.length > 0 && (
                          <Badge variant="secondary">
                            {selectedCharacterIds.length}人選択中
                          </Badge>
                        )}
                      </div>
                      {showCharacterSelector ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </Button>
                    
                    {showCharacterSelector && (
                      <>
                        {/* 検索バー */}
                        {characters.length > 5 && (
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                              type="text"
                              placeholder="名前、役割、年齢などで検索..."
                              value={characterSearchQuery}
                              onChange={(e) => setCharacterSearchQuery(e.target.value)}
                              className="pl-9"
                            />
                          </div>
                        )}
                        
                        {/* キャラクターリスト */}
                        <div className="p-3 border rounded-lg space-y-2 max-h-48 overflow-y-auto">
                          {(() => {
                            const filteredCharacters = characters.filter(character => {
                              if (!characterSearchQuery) return true
                              const query = characterSearchQuery.toLowerCase()
                              return (
                                character.result.name.toLowerCase().includes(query) ||
                                character.result.role.toLowerCase().includes(query) ||
                                character.result.summary.toLowerCase().includes(query)
                              )
                            })
                            
                            return filteredCharacters.length === 0 ? (
                              <p className="text-sm text-muted-foreground text-center py-2">
                                該当するキャラクターが見つかりません
                              </p>
                            ) : (
                              filteredCharacters.map(character => (
                                <div key={character.id} className="flex items-start gap-2">
                                  <Checkbox
                                    id={`char-${character.id}`}
                                    checked={selectedCharacterIds.includes(character.id)}
                                    onCheckedChange={() => handleCharacterToggle(character.id)}
                                  />
                                  <Label 
                                    htmlFor={`char-${character.id}`} 
                                    className="flex-1 cursor-pointer text-sm"
                                  >
                                    <div className="font-medium">{character.result.name}</div>
                                    <div className="text-xs text-muted-foreground">
                                      {character.result.age} / {character.result.role}
                                    </div>
                                  </Label>
                                </div>
                              ))
                            )
                          })()}
                        </div>
                        
                        {selectedCharacterIds.length > 0 && (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Users className="h-4 w-4" />
                            <span>{selectedCharacterIds.length}人のキャラクターを選択中</span>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setSelectedCharacterIds([])
                                updateCharactersText([])
                              }}
                            >
                              クリア
                            </Button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* テキスト入力エリア */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>内容入力</Label>
                  <span className="text-xs text-gray-500">
                    {sourceText.length}文字
                  </span>
                </div>
                <Textarea
                  value={sourceText}
                  onChange={(e) => handleTextChange(e.target.value)}
                  placeholder="タイトルを生成したい内容を入力（プロットやファイルを選択すると自動的に入力されます）"
                  className="min-h-[200px] font-mono text-sm"
                  rows={8}
                />
              </div>
            </div>
            
            {/* 生成設定 */}
            <div className="space-y-4 border-t pt-4">
              <h3 className="font-semibold">生成設定</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>スタイル</Label>
                  <div className="space-y-2 mt-2">
                    <select 
                      className="w-full p-2 border rounded"
                      value={input.style === 'custom' ? 'custom' : input.style}
                      onChange={(e) => {
                        if (e.target.value === 'custom') {
                          // カスタムを選択した場合、styleをcustomに設定して入力欄を表示
                          setInput({ ...input, style: 'custom' })
                          // カスタムスタイルは維持（クリアしない）
                        } else {
                          setCustomStyle('')
                          setInput({ ...input, style: e.target.value })
                        }
                      }}
                    >
                      <option value="impact">インパクト重視</option>
                      <option value="poetic">詩的</option>
                      <option value="simple">シンプル</option>
                      <option value="mysterious">神秘的</option>
                      <option value="epic">壮大</option>
                      <option value="emotional">感情的</option>
                      <option value="custom">
                        {customStyle ? `カスタム: ${customStyle}` : 'カスタム...'}
                      </option>
                    </select>
                    
                    {/* 保存済みカスタムスタイル */}
                    {savedStyles.length > 0 && (
                      <div className="space-y-1">
                        <Label className="text-xs">保存済みスタイル</Label>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {savedStyles.map((style) => (
                            <div 
                              key={style.id} 
                              className="flex items-center justify-between p-2 bg-gray-50 rounded hover:bg-gray-100"
                            >
                              <button
                                className="flex-1 text-left text-sm"
                                onClick={() => handleSelectSavedStyle(style)}
                              >
                                {style.label}
                                <span className="text-xs text-gray-500 ml-2">
                                  (使用: {style.usageCount}回)
                                </span>
                              </button>
                              <button
                                onClick={() => handleDeleteSavedStyle(style.id)}
                                className="p-1 hover:bg-red-100 rounded"
                                title="削除"
                              >
                                <X className="h-3 w-3 text-red-600" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {(customStyle || input.style === 'custom') && (
                      <div className="flex gap-2">
                        <Input
                          placeholder="カスタムスタイルを入力（例：ライトノベル風、純文学風など）"
                          value={customStyle}
                          onChange={(e) => {
                            setCustomStyle(e.target.value)
                            setInput({ ...input, style: e.target.value || 'impact' })
                          }}
                        />
                        <Button 
                          size="sm" 
                          onClick={handleAddCustomStyle}
                          disabled={!customStyle.trim()}
                          title="保存して次回も使えるようにする"
                        >
                          <Save className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <Label>種別</Label>
                  <select 
                    className="w-full mt-2 p-2 border rounded"
                    value={input.titleType}
                    onChange={(e) => setInput({ ...input, titleType: e.target.value as any })}
                  >
                    <option value="work">作品全体</option>
                    <option value="chapter">章</option>
                    <option value="episode">各話</option>
                  </select>
                </div>
              </div>

              <div>
                <Label>創造性レベル: {input.creativityLevel}</Label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={input.creativityLevel}
                  onChange={(e) => setInput({ ...input, creativityLevel: Number(e.target.value) })}
                  className="w-full mt-2"
                />
              </div>

              <div>
                <Label>出力数: {input.outputCount}</Label>
                <input
                  type="range"
                  min="5"
                  max="20"
                  step="5"
                  value={input.outputCount}
                  onChange={(e) => setInput({ ...input, outputCount: Number(e.target.value) })}
                  className="w-full mt-2"
                />
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="pro-mode"
                  checked={input.useProMode}
                  onCheckedChange={(checked) => setInput({ ...input, useProMode: checked as boolean })}
                />
                <Label htmlFor="pro-mode" className="cursor-pointer">
                  Proモード（高精度・低速）
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="gemini3-flash-mode"
                  checked={input.useGemini3FlashMode}
                  onCheckedChange={(checked) => setInput({ ...input, useGemini3FlashMode: checked as boolean })}
                />
                <Label htmlFor="gemini3-flash-mode" className="cursor-pointer">
                  Gemini 3 Flash モード（試験版）
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="useStreaming"
                  checked={useStreaming}
                  onCheckedChange={(checked) => setUseStreaming(checked as boolean)}
                />
                <Label htmlFor="useStreaming" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  ストリーミングを使用（リアルタイム生成・進捗表示）
                </Label>
              </div>
            </div>
            
            <Button 
              onClick={handleGenerate}
              disabled={generator.isPending || streamGenerator.isStreaming || !sourceText.trim()}
              className="w-full"
            >
              {generator.isPending || streamGenerator.isStreaming ? '生成中...' : 'タイトルを生成'}
            </Button>

            {/* エラーメッセージ */}
            {error && (
              <Alert variant="destructive" className="mt-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* ストリーミング進捗表示 */}
            {streamGenerator.isStreaming && (
              <div className="mt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span>生成中...</span>
                  <span>{Math.round(streamGenerator.progress)}%</span>
                </div>
                <Progress value={streamGenerator.progress} />
                {streamGenerator.partialContent && (
                  <div className="mt-4 p-4 bg-muted rounded max-h-48 overflow-y-auto">
                    <pre className="text-xs whitespace-pre-wrap">
                      {streamGenerator.partialContent.slice(-500)}
                      {streamGenerator.partialContent.length > 500 && '...'}
                    </pre>
                  </div>
                )}
              </div>
            )}

            {result && (
              <div className="space-y-3 mt-6">
                <h3 className="font-semibold">生成結果</h3>
                {result.candidates.map((candidate, index) => (
                  <div key={index} className="p-4 border rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-bold text-lg">{candidate.title}</h4>
                        {candidate.reading && (
                          <p className="text-sm text-gray-600">{candidate.reading}</p>
                        )}
                        {candidate.description && (
                          <p className="text-sm mt-2">{candidate.description}</p>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <Badge>{candidate.style}</Badge>
                          {candidate.score && (
                            <Badge variant="secondary">スコア: {candidate.score}</Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => navigator.clipboard.writeText(candidate.title)}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant={isSaved(candidate) ? 'secondary' : 'ghost'}
                          onClick={() => handleSave(candidate)}
                          disabled={isSaved(candidate)}
                          title={isSaved(candidate) ? '保存済み' : '保存'}
                        >
                          <Save className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="saved" className="space-y-4 mt-4">
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFolders(!showFolders)}
                className="px-2"
              >
                <Folder className="h-4 w-4" />
              </Button>
              <ExportImportButtons
                onExportText={exportAsText}
                onExportJSON={exportAsJSON}
                onImportJSON={importFromJSON}
                dataTypeName="タイトル"
              />
            </div>

            {(() => {
              // フォルダによるフィルタリング
              let filteredTitles = savedTitles
              if (selectedFolderId === 'all') {
                filteredTitles = savedTitles
              } else if (selectedFolderId === null) {
                // 未整理: どのフォルダにも属していないアイテム
                const allFolderItemIds = new Set<string>()
                const folders = useFolderStore.getState().getFolders('title')
                folders.forEach(folder => {
                  getItemsInFolder('title', folder.id).forEach(id => allFolderItemIds.add(id))
                })
                filteredTitles = savedTitles.filter(title => !allFolderItemIds.has(title.id))
              } else {
                const itemIds = getItemsInFolder('title', selectedFolderId)
                filteredTitles = savedTitles.filter(title => itemIds.includes(title.id))
              }
              
              return (
                <div className="flex gap-4">
                  {/* フォルダサイドバー */}
                  {showFolders && (
                    <div className="w-48 border-r pr-4">
                      <FolderManager
                        generatorType="title"
                        items={savedTitles}
                        selectedFolderId={selectedFolderId}
                        onFolderSelect={setSelectedFolderId}
                        draggedItemId={draggedItemId}
                        onItemMove={(itemId, toFolderId) => {
                          const currentFolder = getItemFolder('title', itemId)
                          moveItemBetweenFolders('title', itemId, currentFolder?.id || null, toFolderId)
                        }}
                      />
                    </div>
                  )}
                  
                  {/* タイトルリスト */}
                  <div className="flex-1">
                    <ScrollArea className="h-[500px] w-full">
                      {filteredTitles.length > 0 ? (
                        <div className="space-y-3">
                          {filteredTitles.map((saved) => (
                            <div 
                              key={saved.id} 
                              className="p-4 border rounded-lg cursor-move"
                              draggable
                              onDragStart={(e) => {
                                setDraggedItemId(saved.id)
                                e.dataTransfer.effectAllowed = 'move'
                              }}
                              onDragEnd={() => {
                                setDraggedItemId(null)
                              }}
                              style={{
                                opacity: draggedItemId === saved.id ? 0.5 : 1
                              }}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <h4 className="font-bold">{saved.title}</h4>
                                  <div className="flex items-center gap-2 mt-1">
                                    <Badge variant="outline">{saved.input.style}</Badge>
                                    <Badge variant="secondary">{saved.input.titleType}</Badge>
                                    <span className="text-xs text-gray-500">
                                      {new Date(saved.createdAt).toLocaleDateString()}
                                    </span>
                                  </div>
                                  {/* フォルダ選択 */}
                                  <div className="mt-2 w-48">
                                    <FolderMoveSelect
                                      generatorType="title"
                                      itemId={saved.id}
                                      currentFolderId={getItemFolder('title', saved.id)?.id}
                                      onMove={() => {}}
                                    />
                                  </div>
                                </div>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => deleteTitle(saved.id)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          保存済みタイトルがありません
                        </div>
                      )}
                    </ScrollArea>
                  </div>
                </div>
              )
            })()}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}